/**
 * Merchant Insights — iteration 35f
 * Category-median peer benchmarks. Scale tier only. When merchant is on a lower
 * tier, backend returns 402 with an upsell payload — we render a locked state
 * with a CTA to /merchant/billing.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { cad } from "@/lib/pricing";
import { PlanUpgradeNotice, isPlanUpgradeError } from "@/components/merchant/PlanUpgradeNotice";
import { Sparkles, TrendingUp, Percent, DollarSign, Trophy, Lock, ArrowRight, Users, ArrowUpRight, ArrowDownRight } from "lucide-react";

const fmtMoney = (n) => cad(n || 0);
const category = (c) => (c || "general").replace(/[_-]+/g, " ").replace(/^./, (x) => x.toUpperCase());
const fmtPct = (n) => `${Number(n || 0).toFixed(2)}%`;

/**
 * Tiny inline SVG sparkline — no lib required. Draws a stroke path from a
 * numeric series, colored by delta direction.
 */
function Sparkline({ series, positive, testid }) {
  if (!series || series.length < 2) {
    return <div className="h-6 text-[10px] text-slate-300 italic" data-testid={testid}>Trend arrives after a few days</div>;
  }
  const w = 96, h = 24, pad = 2;
  const min = Math.min(...series);
  const max = Math.max(...series);
  const range = max - min || 1;
  const step = (w - pad * 2) / (series.length - 1);
  const pts = series.map((v, i) => {
    const x = pad + i * step;
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const stroke = positive == null ? "#94a3b8" : (positive ? "#10b981" : "#ef4444");
  const fill = positive == null ? "rgba(148,163,184,0.15)" : (positive ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.15)");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="overflow-visible" data-testid={testid}>
      <polyline points={`${pad},${h - pad} ${pts.join(" ")} ${w - pad},${h - pad}`} fill={fill} stroke="none" />
      <polyline points={pts.join(" ")} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={pts[pts.length - 1].split(",")[0]} cy={pts[pts.length - 1].split(",")[1]} r="1.75" fill={stroke} />
    </svg>
  );
}

function DeltaPill({ deltaPct, days, testid }) {
  if (deltaPct == null) return null;
  const up = deltaPct > 0;
  const flat = Math.abs(deltaPct) < 0.05;
  const tone = flat ? "bg-slate-100 text-slate-500" : (up ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700");
  const Icon = flat ? null : (up ? ArrowUpRight : ArrowDownRight);
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${tone}`}
          data-testid={testid} title={`Category median change over the last ${days || 7} days`}>
      {Icon && <Icon className="w-2.5 h-2.5" />} {flat ? "flat" : `${up ? "+" : ""}${deltaPct.toFixed(1)}%`}
    </span>
  );
}

function BenchmarkCard({ icon: Icon, label, unit, benchmarks, yourValue, series, deltaPct, days, testid }) {
  if (!benchmarks) return null;
  const fmt = unit === "money" ? fmtMoney : unit === "pct" ? fmtPct : (v) => Number(v || 0).toLocaleString();
  const yourInRange = yourValue != null;
  const positive = deltaPct == null ? null : deltaPct > 0;
  return (
    <div className="pf-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-cyan-50 border border-cyan-100 text-cyan-700"><Icon className="w-4 h-4" /></div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        </div>
        <DeltaPill deltaPct={deltaPct} days={days} testid={`${testid}-delta`} />
      </div>
      <Sparkline series={series} positive={positive} testid={`${testid}-sparkline`} />
      <div className="grid grid-cols-4 gap-2 text-center mt-3">
        {[["p25","p25"],["median","med"],["p75","p75"],["p90","p90"]].map(([k, short]) => (
          <div key={k}>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{short}</div>
            <div className="text-sm font-display text-slate-900 tabular-nums mt-0.5" data-testid={`${testid}-${k}`}>
              {fmt(benchmarks[k])}
            </div>
          </div>
        ))}
      </div>
      {yourInRange && (
        <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest text-cyan-700 font-semibold">You</span>
          <span className="text-lg font-display text-cyan-700 tabular-nums" data-testid={`${testid}-you`}>{fmt(yourValue)}</span>
        </div>
      )}
    </div>
  );
}

function LockedState({ detail }) {
  const preview = detail?.preview;
  return (
    <div className="max-w-2xl mx-auto p-8 rounded-2xl bg-gradient-to-br from-slate-50 to-cyan-50 border border-slate-200 text-center relative overflow-hidden"
         data-testid="insights-locked">
      <div className="absolute -top-16 -right-16 w-64 h-64 pf-gradient-bg rounded-full blur-3xl opacity-30" />
      <div className="relative">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white border border-cyan-200 mb-4">
          <Lock className="w-6 h-6 text-cyan-600" />
        </div>
        <div className="inline-flex items-center gap-1.5 text-cyan-700 text-[10px] uppercase tracking-widest font-semibold">
          <Sparkles className="w-3.5 h-3.5" /> Paid plan feature
        </div>
        <h2 className="font-display text-3xl text-slate-900 mt-2">See how you stack up</h2>
        <p className="text-sm text-slate-600 mt-3 max-w-md mx-auto leading-relaxed">
          Merchant Insights shows category-wide GMV, conversion-rate, and AOV benchmarks — plus your rank and percentile. Aggregate-only, no competitor names are ever shown.
        </p>

        {preview && preview.would_rank != null && (
          <div className="mt-6 mx-auto max-w-md rounded-2xl border border-cyan-200 bg-white/70 backdrop-blur-sm p-5 text-left shadow-sm"
               data-testid="insights-locked-fomo">
            <div className="inline-flex items-center gap-1.5 text-emerald-700 text-[10px] uppercase tracking-widest font-semibold">
              <Trophy className="w-3.5 h-3.5" /> Sneak peek
            </div>
            <div className="mt-2 text-lg text-slate-900">
              You'd rank{" "}
              <span className="pf-gradient-text-2 font-display text-2xl align-middle" data-testid="insights-locked-fomo-rank">
                #{preview.would_rank}
              </span>{" "}
              out of <span className="font-semibold">{preview.peers_count}</span> in{" "}
              <span className="capitalize font-semibold">{preview.category}</span>
            </div>
            {preview.would_percentile != null && (
              <div className="text-xs text-slate-500 mt-1">
                That's the <strong>{preview.would_percentile}th</strong> percentile — see the full quartile breakdown, momentum trend, and settlement math when you upgrade.
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex items-center justify-center gap-2">
          <Link to="/merchant/billing" className="pf-btn-primary px-6 py-3 rounded-full font-semibold text-sm inline-flex items-center gap-2"
                data-testid="insights-upgrade-cta">
            Change plan <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="mt-4 text-[11px] text-slate-500">
          Currently on: <span className="font-semibold">{category(detail?.current_tier || "starter")}</span>
        </div>
      </div>
    </div>
  );
}

export default function MerchantInsights() {
  const [data, setData] = useState(null);
  const [locked, setLocked] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planLock, setPlanLock] = useState(null);

  useEffect(() => {
    api.get("/merchant/entitlements")
      .then((r) => r.data?.plan === "basic" && !r.data?.advanced_analytics)
      .catch(() => false)
      .then((basic) => {
        if (basic) {
          setPlanLock({ message: "Category benchmarks come with the Starter and Pro plans. Change your plan to see how you compare." });
          return null;
        }
        return api.get("/merchant/insights")
          .then(r => setData(r.data))
          .catch(e => {
            if (isPlanUpgradeError(e)) setPlanLock(e.response.data.detail);
            else if (e.response?.status === 402) setLocked(e.response.data?.detail || {});
          });
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-center text-slate-400 text-sm">Loading insights…</div>;
  if (planLock) return <PlanUpgradeNotice detail={planLock} testid="insights-plan-locked" />;
  if (locked) return <LockedState detail={locked} />;
  if (!data) return <div className="p-8 text-center text-slate-500">No insights available.</div>;

  if (!data.ready) {
    return (
      <div className="max-w-xl mx-auto p-8 text-center" data-testid="insights-not-ready">
        <Sparkles className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
        <h2 className="text-xl font-display text-slate-900">Benchmarks are being generated</h2>
        <p className="text-sm text-slate-500 mt-2" data-testid="insights-not-ready-body">We publish category benchmarks once there is enough activity from at least 5 merchants in your category. Snapshots refresh every night, so check back soon.</p>
        <p className="text-xs text-slate-400 mt-2" data-testid="insights-not-ready-category">Your category: <span className="font-semibold text-slate-600">{category(data.category)}</span></p>
      </div>
    );
  }

  const your = data.your_stats || {};
  return (
    <div data-testid="merchant-insights">
      <div className="mb-6">
        <div className="inline-flex items-center gap-1.5 text-cyan-700 text-[10px] uppercase tracking-widest font-semibold">
          <Trophy className="w-3.5 h-3.5" /> Merchant insights
        </div>
        <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight text-slate-900 mt-2">
          How you stack up in <span className="pf-gradient-text-2 capitalize">{data.category}</span>
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Snapshot from {data.snapshot_date} · {data.window_days}-day window · {data.peers?.merchants_count} merchants in your category.
        </p>
      </div>

      {/* Your rank card */}
      {your.rank != null && (
        <div className="pf-card p-6 mb-6 bg-gradient-to-br from-cyan-50 to-emerald-50 border border-cyan-100" data-testid="insights-your-rank">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-cyan-700 font-semibold">Your rank</div>
              <div className="font-display text-3xl text-slate-900 tabular-nums mt-1" data-testid="insights-rank-value">
                #{your.rank}
                <span className="text-sm text-slate-500 font-normal ml-2">of {data.peers?.merchants_count}</span>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-cyan-700 font-semibold">Percentile</div>
              <div className="font-display text-3xl text-slate-900 tabular-nums mt-1" data-testid="insights-percentile-value">
                {your.percentile}%
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {your.percentile >= 90 ? "Top decile — you're a category leader." :
                 your.percentile >= 75 ? "Top quartile — outperforming most peers." :
                 your.percentile >= 50 ? "Above median for the category." :
                 "Room to grow — see benchmarks below."}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-cyan-700 font-semibold">Your 30d GMV</div>
              <div className="font-display text-3xl text-slate-900 tabular-nums mt-1">{fmtMoney(your.gmv)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Benchmark cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <BenchmarkCard icon={DollarSign} label="GMV distribution" unit="money"
                       benchmarks={data.peers?.gmv} yourValue={your.gmv}
                       series={(data.trend || []).map(t => t.gmv_median)}
                       deltaPct={data.trend_deltas?.gmv_pct_change}
                       days={data.trend_deltas?.days}
                       testid="insights-benchmark-gmv" />
        <BenchmarkCard icon={Percent} label="Conversion rate" unit="pct"
                       benchmarks={data.peers?.cvr} yourValue={null}
                       series={(data.trend || []).map(t => t.cvr_median)}
                       deltaPct={data.trend_deltas?.cvr_pct_change}
                       days={data.trend_deltas?.days}
                       testid="insights-benchmark-cvr" />
        <BenchmarkCard icon={TrendingUp} label="Average order value" unit="money"
                       benchmarks={data.peers?.aov} yourValue={null}
                       series={(data.trend || []).map(t => t.aov_median)}
                       deltaPct={data.trend_deltas?.aov_pct_change}
                       days={data.trend_deltas?.days}
                       testid="insights-benchmark-aov" />
      </div>

      <div className="mt-6 p-4 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-500 flex items-start gap-2">
        <Users className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
        <div>
          Benchmarks are aggregated across all {data.peers?.merchants_count} merchants in <strong>{data.category}</strong>.
          Individual merchant identities are never shown. Requires at least 5 peers per category to publish (privacy floor).
          Snapshots refresh nightly at 03:00 UTC.
        </div>
      </div>
    </div>
  );
}
